import { useFingerprint, useFormContext } from 'Context';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { CustomerApiClient } from 'Services';

const FINGERPRINT_POLL_INTERVAL_MS = 60000;
const PENDING_RETRY_DELAY_MS = 7000;

function getFingerprintKey(
  visitorId?: string | null,
  requestId?: string | null
) {
  if (!visitorId || !requestId) {
    return null;
  }

  return `${visitorId}:${requestId}`;
}

export function useVpnCheck() {
  const form = useFormContext();
  const { getVisitorOptions } = useFingerprint();
  const { search } = useLocation();

  const submissionId = form?.stateDelegate?.submissionId;
  const customerVerificationId = useMemo(
    () =>
      new URLSearchParams(search).get('customer_verification_id') || undefined,
    [search]
  );

  const [isVpnActive, setIsVpnActive] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [vpnPopupInstanceId, setVpnPopupInstanceId] = useState<number | null>(
    null
  );

  const lastFingerprintRef = useRef<string | null>(null);
  const pendingTimeoutRef = useRef<number>();
  const fingerprintPollRef = useRef<number>();
  const isMountedRef = useRef(true);
  const isRequestInFlightRef = useRef(false);
  const isVpnDetectionDisabledRef = useRef(false);

  const clearPendingTimeout = useCallback(() => {
    if (pendingTimeoutRef.current) {
      window.clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = undefined;
    }
  }, []);

  const canRunCheck = Boolean(submissionId || customerVerificationId);

  const runCheck = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (
        !canRunCheck ||
        isRequestInFlightRef.current ||
        isVpnDetectionDisabledRef.current
      ) {
        return;
      }

      const fingerprint = await getVisitorOptions() as any;
      const fingerprintKey = getFingerprintKey(
        fingerprint?.visitorId,
        fingerprint?.requestId
      );

      if (
        fingerprintKey &&
        !force &&
        fingerprintKey === lastFingerprintRef.current
      ) {
        return;
      }

      isRequestInFlightRef.current = true;
      clearPendingTimeout();

      if (isMountedRef.current) {
        setIsChecking(true);
      }

      try {
        const fingerprintPayload =
          fingerprint?.visitorId && fingerprint?.requestId
            ? {
                fingerprintjs_visitor_id: fingerprint.visitorId,
                fingerprintjs_request_id: fingerprint.requestId,
              }
            : {};

        const result = await CustomerApiClient.vpnDetection(
          customerVerificationId
            ? {
                customer_verification_id: customerVerificationId,
                ...fingerprintPayload,
              }
            : {
                submission_id: submissionId as string,
                ...fingerprintPayload,
              }
        );

        if (!isMountedRef.current) {
          return;
        }

        const status = result.data?.status;

        if (!status || status === 'unknown') {
          form.disabled = false;
          return;
        }

        if (fingerprintKey) {
          lastFingerprintRef.current = fingerprintKey;
        }

        switch (status) {
          case 'no_vpn_detected':
            form.disabled = false;
            setIsVpnActive(false);
            break;
          case 'vpn_detected':
            form.disabled = true;
            setIsVpnActive(true);
            setVpnPopupInstanceId(Date.now());
            break;
          case 'vpn_detection_disabled':
            form.disabled = false;
            isVpnDetectionDisabledRef.current = true;
            setIsVpnActive(false);
            clearPendingTimeout();

            if (fingerprintPollRef.current) {
              window.clearInterval(fingerprintPollRef.current);
              fingerprintPollRef.current = undefined;
            }
            break;
          case 'pending':
            form.disabled = false;
            pendingTimeoutRef.current = window.setTimeout(() => {
              runCheck({ force: true });
            }, PENDING_RETRY_DELAY_MS);
            break;
        }
      } catch (e) {
        console.error(e);
      } finally {
        isRequestInFlightRef.current = false;

        if (isMountedRef.current) {
          setIsChecking(false);
        }
      }
    },
    [
      canRunCheck,
      submissionId,
      customerVerificationId,
      getVisitorOptions,
      clearPendingTimeout,
      form,
    ]
  );

  const recheck = useCallback(() => runCheck({ force: true }), [runCheck]);

  useEffect(() => {
    isMountedRef.current = true;

    if (canRunCheck) {
      runCheck({ force: true });
    }

    fingerprintPollRef.current = window.setInterval(() => {
      if (!isVpnDetectionDisabledRef.current) {
        runCheck();
      }
    }, FINGERPRINT_POLL_INTERVAL_MS);

    const handleOnline = () => {
      if (!isVpnDetectionDisabledRef.current) {
        runCheck({ force: true });
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('online', handleOnline);
      clearPendingTimeout();

      if (fingerprintPollRef.current) {
        window.clearInterval(fingerprintPollRef.current);
      }
    };
  }, [canRunCheck, runCheck, clearPendingTimeout]);

  return {
    isVpnActive,
    isChecking,
    recheck,
    vpnPopupInstanceId,
  };
}

export * from './types';
